import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { calculateScenario, createBaselineScenario } from '../domain/scenarioEngine';
import type {
  FinanceData,
  SavedScenario,
  ScenarioInputs,
  ScenarioResult,
} from '../domain/types';

interface ScenarioContextValue {
  data: FinanceData;
  inputs: ScenarioInputs;
  result: ScenarioResult;
  savedScenarios: SavedScenario[];
  archivedPersonnelIds: string[];
  setInputs: React.Dispatch<React.SetStateAction<ScenarioInputs>>;
  updateInputs: (changes: Partial<ScenarioInputs>) => void;
  resetScenario: () => void;
  saveScenario: () => void;
  loadScenario: (scenario: SavedScenario) => void;
  deleteScenario: (id: string) => void;
  archivePersonnel: (id: string) => void;
  archivePersonnelMany: (ids: string[]) => void;
  restorePersonnel: (id: string) => void;
  restoreAllPersonnel: () => void;
}

const ScenarioContext = createContext<ScenarioContextValue | null>(null);
const STORAGE_KEY = 'deverus.finance.scenarios.v2';
// The confirmed 2026 roster supersedes the prior legacy-record cleanup state.
const ARCHIVED_PERSONNEL_KEY = 'deverus.finance.archived-personnel.v3';

interface StoredScenario {
  id: string;
  name: string;
  savedAt: string;
  inputs: ScenarioInputs;
  datasetVersion: string;
}

interface StoredWorkspace {
  version: 2;
  datasetVersion: string;
  current: ScenarioInputs;
  saved: StoredScenario[];
}

const cloneInputs = (inputs: ScenarioInputs): ScenarioInputs =>
  JSON.parse(JSON.stringify(inputs)) as ScenarioInputs;

const normalizeInputs = (inputs: ScenarioInputs): ScenarioInputs => ({
  ...inputs,
  personnelAdjustments: inputs.personnelAdjustments ?? {},
  clientRevenueAdjustments: inputs.clientRevenueAdjustments ?? {},
});

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const isScenarioInputs = (value: unknown): value is ScenarioInputs => {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<ScenarioInputs>;
  return Boolean(
    typeof candidate.name === 'string' &&
    candidate.expenseAdjustments && typeof candidate.expenseAdjustments === 'object' &&
    candidate.revenueAdjustments && typeof candidate.revenueAdjustments === 'object' &&
    isFiniteNumber(candidate.ownerDrawMonthly) &&
    isFiniteNumber(candidate.arRecoveryPercent) &&
    isFiniteNumber(candidate.arRecoveryMonths) &&
    isFiniteNumber(candidate.debtPrepayment),
  );
};

const restoreWorkspace = (datasetVersion: string): StoredWorkspace | null => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const value = JSON.parse(raw) as Partial<StoredWorkspace>;
    if (value.version !== 2 || value.datasetVersion !== datasetVersion || !isScenarioInputs(value.current) || !Array.isArray(value.saved)) {
      return null;
    }
    const saved = value.saved.filter((entry): entry is StoredScenario => Boolean(
      entry &&
      typeof entry.id === 'string' &&
      typeof entry.name === 'string' &&
      typeof entry.savedAt === 'string' &&
      entry.datasetVersion === datasetVersion &&
      isScenarioInputs(entry.inputs),
    ));
    return {
      version: 2,
      datasetVersion,
      current: normalizeInputs(value.current),
      saved: saved.map((entry) => ({ ...entry, inputs: normalizeInputs(entry.inputs) })),
    };
  } catch {
    return null;
  }
};

const restoreArchivedPersonnel = (datasetVersion: string): string[] => {
  try {
    const value = JSON.parse(window.localStorage.getItem(ARCHIVED_PERSONNEL_KEY) ?? '{}') as { datasetVersion?: string; ids?: unknown };
    return value.datasetVersion === datasetVersion && Array.isArray(value.ids)
      ? [...new Set(value.ids.filter((id): id is string => typeof id === 'string'))]
      : [];
  } catch {
    return [];
  }
};

export const ScenarioProvider = ({
  data,
  children,
}: {
  data: FinanceData;
  children: ReactNode;
}) => {
  const datasetVersion = data.datasetVersion ?? data.lastSyncedAt;
  const restored = useMemo(() => restoreWorkspace(datasetVersion), [datasetVersion]);
  const [inputs, setInputs] = useState<ScenarioInputs>(() => restored?.current ?? createBaselineScenario(data));
  const [savedRecords, setSavedRecords] = useState<StoredScenario[]>(() => restored?.saved ?? []);
  const [archivedPersonnelIds, setArchivedPersonnelIds] = useState<string[]>(() => restoreArchivedPersonnel(datasetVersion));
  const result = useMemo(() => calculateScenario(data, inputs), [data, inputs]);
  const savedScenarios = useMemo<SavedScenario[]>(
    () => savedRecords.map((scenario) => ({
      ...scenario,
      summary: calculateScenario(data, scenario.inputs).scenario,
    })),
    [data, savedRecords],
  );

  useEffect(() => {
    const value: StoredWorkspace = {
      version: 2,
      datasetVersion,
      current: cloneInputs(inputs),
      saved: savedRecords.map((scenario) => ({ ...scenario, inputs: cloneInputs(scenario.inputs) })),
    };
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    } catch {
      // Local storage may be unavailable in private browsing; the workspace still works in memory.
    }
  }, [inputs, savedRecords]);

  useEffect(() => {
    try {
      window.localStorage.setItem(ARCHIVED_PERSONNEL_KEY, JSON.stringify({ datasetVersion, ids: archivedPersonnelIds }));
    } catch {
      // Archiving remains available in memory when local storage is unavailable.
    }
  }, [archivedPersonnelIds, datasetVersion]);

  const updateInputs = useCallback((changes: Partial<ScenarioInputs>) => {
    setInputs((current) => ({ ...current, ...changes }));
  }, []);

  const resetScenario = useCallback(() => {
    setInputs(createBaselineScenario(data));
  }, [data]);

  const saveScenario = useCallback(() => {
    const id = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}`;
    setSavedRecords((current) => [
      ...current,
      {
        id,
        name: inputs.name.trim() || `Scenario ${current.length + 1}`,
        savedAt: new Date().toISOString(),
        datasetVersion,
        inputs: cloneInputs(inputs),
      },
    ]);
  }, [datasetVersion, inputs]);

  const loadScenario = useCallback((scenario: SavedScenario) => {
    setInputs(cloneInputs(scenario.inputs));
  }, []);

  const deleteScenario = useCallback((id: string) => {
    setSavedRecords((current) => current.filter((scenario) => scenario.id !== id));
  }, []);

  const archivePersonnelMany = useCallback((ids: string[]) => {
    const idSet = new Set(ids);
    if (!idSet.size) return;
    setArchivedPersonnelIds((current) => [...new Set([...current, ...idSet])]);
    setInputs((current) => ({
      ...current,
      personnelAdjustments: Object.fromEntries(
        Object.entries(current.personnelAdjustments ?? {}).filter(([id]) => !idSet.has(id)),
      ),
    }));
  }, []);

  const archivePersonnel = useCallback((id: string) => archivePersonnelMany([id]), [archivePersonnelMany]);

  const restorePersonnel = useCallback((id: string) => {
    setArchivedPersonnelIds((current) => current.filter((personnelId) => personnelId !== id));
  }, []);

  const restoreAllPersonnel = useCallback(() => setArchivedPersonnelIds([]), []);

  const value = useMemo(
    () => ({
      data,
      inputs,
      result,
      savedScenarios,
      archivedPersonnelIds,
      setInputs,
      updateInputs,
      resetScenario,
      saveScenario,
      loadScenario,
      deleteScenario,
      archivePersonnel,
      archivePersonnelMany,
      restorePersonnel,
      restoreAllPersonnel,
    }),
    [
      data,
      archivePersonnel,
      archivePersonnelMany,
      archivedPersonnelIds,
      deleteScenario,
      inputs,
      loadScenario,
      resetScenario,
      result,
      saveScenario,
      savedScenarios,
      restoreAllPersonnel,
      restorePersonnel,
      updateInputs,
    ],
  );

  return <ScenarioContext.Provider value={value}>{children}</ScenarioContext.Provider>;
};

export const useScenario = () => {
  const value = useContext(ScenarioContext);
  if (!value) throw new Error('useScenario must be used inside ScenarioProvider');
  return value;
};

export const clearFinanceWorkspaceStorage = () => {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
    window.localStorage.removeItem(ARCHIVED_PERSONNEL_KEY);
    window.localStorage.removeItem('deverus.finance.scenarios.v1');
    window.localStorage.removeItem('deverus.finance.archived-personnel.v2');
  } catch {
    // The server session still ends when browser storage is unavailable.
  }
};
